import { ReactElement, ReactNode, useCallback } from 'react'

import { LocaleIdentifier } from '../../types/localeIdentifier'
import { AmountValue } from '../AmountValue'
import { BadgeColor } from '../Badge'
import { BadgeWithLabel } from '../BadgeWithLabel'
import { SkeletonText } from '../SkeletonText'
import * as Styled from './styles'

type Id = string | number

export interface AmountInfoBoxPropsBase<T> {
  active?: boolean
  amount?: number
  badgeAmount?: number
  badgeLabel?: ReactNode
  badgeColor?: BadgeColor
  badgeOnly?: boolean
  clickable?: boolean
  currencyId?: string
  disabled?: boolean
  id?: T
  loading?: boolean
  locale: LocaleIdentifier
  onClick?: (id?: T) => void
  shortFormat?: boolean
}

export interface AmountInfoBoxPropsBaseId<T> extends Omit<AmountInfoBoxPropsBase<T>, 'onClick' | 'id'> {
  id: T
  onClick?: (id: T) => void
}

export type AmountInfoBoxProps<T = unknown> = T extends Id ? AmountInfoBoxPropsBaseId<T> : AmountInfoBoxPropsBase<T>

export const AmountInfoBox = <T,>({
  active,
  amount,
  badgeAmount,
  badgeLabel,
  badgeColor = 'accent',
  badgeOnly = false,
  clickable,
  currencyId,
  disabled,
  id,
  loading,
  locale,
  onClick,
  shortFormat = true,
  ...rest
}: AmountInfoBoxProps<T>): ReactElement => {
  const shouldShowAmount = badgeOnly === false
  const handleClick = useCallback(() => {
    if (clickable && !disabled) {
      onClick?.(id as T)
    }
  }, [clickable, id, disabled, onClick])

  return (
    <Styled.AmountInfoBoxWrapper
      active={active}
      badgeOnly={badgeOnly}
      clickable={clickable}
      disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      <Styled.BadgeWithLabelWrapper>
        {loading ? (
          <SkeletonText variant="bodySM" width={80} />
        ) : (
          <BadgeWithLabel color={badgeColor} badge={badgeAmount}>
            {badgeLabel}
          </BadgeWithLabel>
        )}
      </Styled.BadgeWithLabelWrapper>

      {shouldShowAmount && loading ? (
        <SkeletonText variant="h1" fullWidth />
      ) : (
        <>
          {shouldShowAmount && amount !== undefined && (
            <AmountValue
              amount={amount}
              currencyId={currencyId}
              locale={locale}
              shortFormat={shortFormat}
              textVariant="h1"
            />
          )}
        </>
      )}
    </Styled.AmountInfoBoxWrapper>
  )
}
